import type { DeliveryMarkerKind, RiderMarkerFreshness } from "./tracking-map-state";

// =============================================================================
// Issue #81 — Self-identifying Delivery Tracking markers and provisional legend
//
// Builds a custom DOM element for each delivery tracking marker kind so the
// customer can tell origin, destination, and Rider apart at a glance without
// relying on MapLibre's default colored droplet. The element is intentionally
// replaceable so future illustrated pins can supersede these placeholders
// without touching the tracking data contract.
//
// Security note: SVG bodies are inline constant strings controlled by the
// application — they do not interpolate user-provided data. The only piece of
// data the marker takes from the caller is `accessibleLabel`, which is set via
// `setAttribute("aria-label", ...)` (and never via `innerHTML`) so it is
// rendered as a literal string.
// =============================================================================

const SVG_NS = "http://www.w3.org/2000/svg";

const MARKER_SVG: Record<DeliveryMarkerKind, string> = {
  // Store / local — a shopping bag silhouette.
  origin:
    '<path d="M7 8h10l-1 12H8L7 8z" />' +
    '<path d="M9.5 8V6.2a2.5 2.5 0 0 1 5 0V8" fill="none" stroke-linecap="round" />',
  // Home / destination — a house outline with a roof.
  destination:
    '<path d="M5 11.5 12 5l7 6.5" fill="none" stroke-linecap="round" stroke-linejoin="round" />' +
    '<path d="M6.5 10.5V19h11v-8.5" />' +
    '<path d="M10.5 19v-5h3v5" fill="none" />',
  // Rider / movement — a navigation arrow that reads as direction of travel.
  rider: '<path d="M12 4 6 18l6-3.5L18 18 12 4z" />' + '<circle cx="12" cy="4" r="1.4" />',
};

const MARKER_BASE_CLASS = "delivery-marker";
const MARKER_STALE_CLASS = "delivery-marker--stale";

export type CreateDeliveryMarkerElementArgs = {
  kind: DeliveryMarkerKind;
  freshness: RiderMarkerFreshness | undefined;
  accessibleLabel: string;
};

export function createDeliveryMarkerElement(args: CreateDeliveryMarkerElementArgs): HTMLDivElement {
  const el = document.createElement("div");
  el.className = MARKER_BASE_CLASS;
  el.setAttribute("data-marker-kind", args.kind);
  el.setAttribute("role", "img");
  // aria-label is the only user-controlled string on the marker; setting it
  // through setAttribute keeps the value as a literal attribute value rather
  // than parsing it as HTML.
  el.setAttribute("aria-label", args.accessibleLabel);

  if (args.kind === "rider" && args.freshness) {
    el.setAttribute("data-marker-freshness", args.freshness);
    if (args.freshness === "stale") {
      el.classList.add(MARKER_STALE_CLASS);
    }
  }

  el.appendChild(createMarkerSvg(args.kind));
  return el;
}

function createMarkerSvg(kind: DeliveryMarkerKind): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "24");
  svg.setAttribute("height", "24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  // SVG body is a constant per kind — never derived from user data.
  svg.innerHTML = MARKER_SVG[kind];
  return svg;
}

// -----------------------------------------------------------------------------
// Popup content
// -----------------------------------------------------------------------------

export const DELIVERY_MARKER_POPUP_VARIANTS = [
  "origin",
  "destination",
  "riderLive",
  "lastKnown",
] as const;

export type DeliveryMarkerPopupVariant = (typeof DELIVERY_MARKER_POPUP_VARIANTS)[number];

export type CreateDeliveryMarkerPopupContentArgs = {
  variant: DeliveryMarkerPopupVariant;
  copy: string;
};

export function createDeliveryMarkerPopupContent(
  args: CreateDeliveryMarkerPopupContentArgs,
): HTMLDivElement {
  const el = document.createElement("div");
  el.className = "delivery-marker-popup";
  el.setAttribute("data-popup-variant", args.variant);
  // Copy is set via textContent so any HTML-looking characters render as text
  // rather than being parsed as markup.
  el.textContent = args.copy;
  return el;
}

// -----------------------------------------------------------------------------
// Labels contract for the legend / popups
// -----------------------------------------------------------------------------

export type DeliveryTrackingMarkerLabels = {
  origin: string;
  destination: string;
  riderLive: string;
  riderStale: string;
};

export type DeliveryTrackingPopupLabels = {
  origin: string;
  destination: string;
  riderLive: string;
  lastKnown: string;
};
